// Registra, de forma mínima, sessões e atividade do usuário logado (painel de
// adesão). Não renderiza nada. Falhas são silenciosas: o rastreio nunca pode
// atrapalhar o uso do app (ex.: migração 0031 ainda não aplicada).
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { HEARTBEAT_MS, IDLE_MS, sectionOf, sendUsageEvent } from '../lib/usage';

const SESSION_KEY = 'npd.usageSession';

function getSessionId(): { id: string; isNew: boolean } {
  try {
    const cur = sessionStorage.getItem(SESSION_KEY);
    if (cur) return { id: cur, isNew: false };
    const id = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, id);
    return { id, isNew: true };
  } catch {
    return { id: crypto.randomUUID(), isNew: true };
  }
}

export function UsageTracker() {
  const { userId, currentOrgId } = useAuth();
  const { pathname } = useLocation();
  const section = useRef(sectionOf(pathname));
  section.current = sectionOf(pathname);

  useEffect(() => {
    if (!userId || !currentOrgId) return;
    const { id, isNew } = getSessionId();
    let lastActivity = Date.now();
    let broken = false;

    const send = async (kind: 'open' | 'heartbeat') => {
      if (broken) return;
      try {
        const ok = await sendUsageEvent(currentOrgId, userId, id, kind, section.current);
        if (!ok) broken = true; // tabela ausente/sem permissão: para de tentar nesta sessão
      } catch {
        broken = true;
      }
    };

    if (isNew) void send('open');

    const touch = () => {
      lastActivity = Date.now();
    };
    const events = ['pointerdown', 'keydown', 'scroll', 'touchstart'] as const;
    events.forEach((e) => window.addEventListener(e, touch, { passive: true }));

    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastActivity > IDLE_MS) return;
      void send('heartbeat');
    }, HEARTBEAT_MS);

    return () => {
      window.clearInterval(timer);
      events.forEach((e) => window.removeEventListener(e, touch));
    };
  }, [userId, currentOrgId]);

  return null;
}
